<template>
  <div class="section-list-strategy-selector w-full">
    <NSelect
      :options="strategyOptions"
      :render-label="renderLabel"
      :value="
        modelValue?.strategy === 'app'
          ? sectionSelectorApps.findIndex((app) => app.app_metadata?.name === modelValue?.appName)
          : modelValue?.strategy
      "
      @update:value="handleStrategyChange"
    />

    <div v-if="modelValue?.strategy === 'single' || modelValue?.strategy === 'at-least-one'" class="mt-2">
      <NCheckbox v-model:checked="isRestrictedToPreselections" @update:checked="handleRestrictUpdate">
        {{ t('canvasUI.strategy.restrictToPreselections') }}
      </NCheckbox>
    </div>

    <NModal
      v-model:show="isCustomModalOpen"
      class="w-screen h-screen max-w-none"
      preset="card"
      :title="t('canvasUI.strategy.customTitle')"
    >
      <iframe ref="iframeRef" class="w-full h-full border-none" :src="selectedAppUrl" />
    </NModal>
  </div>
</template>

<script setup lang="ts">
import { ref, inject, computed, h, nextTick, type ComputedRef, onMounted } from 'vue'
import { NSelect, NModal, NButton, NCheckbox } from 'naive-ui'
import { useI18n } from 'vue-i18n'
import { doesLocalOverrideExist, getLocalOverrideUrl } from '@lib/util/dev.util'
import { waitForIframeInitialize } from '@lib/utils/iframeInitialize'

import type { AppFile } from '@lib/types/app'

interface StrategyValue {
  strategy: string
  appName?: string
  is_restricted_to_preselections?: boolean
}

interface Props {
  modelValue?: StrategyValue | null
  canvasId?: string
  sectionListData?: any
  sectionListId?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => ({ strategy: 'free' }),
})

const emit = defineEmits<{
  'update:modelValue': [value: StrategyValue]
}>()

const sectionSelectorApps = inject<ComputedRef<AppFile[]>>(
  'sectionSelectorApps',
  computed(() => []),
)

const { t } = useI18n()
const selectedAppUrl = ref('')
const isCustomModalOpen = ref(false)
const iframeRef = ref<HTMLIFrameElement | null>(null)
const isRestrictedToPreselections = ref(false)

const STRATEGIES = [
  { value: 'free', label: t('canvasUI.strategy.free'), isApp: false },
  { value: 'single', label: t('canvasUI.strategy.singleSection'), isApp: false },
  { value: 'at-least-one', label: t('canvasUI.strategy.atLeastOne'), isApp: false },
] as const

const strategyOptions = computed(() => [
  ...STRATEGIES,
  ...sectionSelectorApps.value.map((app, index) => ({
    value: index,
    label: app.app_metadata?.display_name ?? '',
    isApp: true,
  })),
])

function handleStrategyChange(strategyOrAppIndex: string | number) {
  if (typeof strategyOrAppIndex === 'number') {
    const appName = sectionSelectorApps.value[strategyOrAppIndex].app_metadata?.name
    emit('update:modelValue', {
      strategy: 'app',
      appName,
      is_restricted_to_preselections: isRestrictedToPreselections.value,
    })
  } else {
    emit('update:modelValue', {
      strategy: strategyOrAppIndex,
      is_restricted_to_preselections: isRestrictedToPreselections.value,
    })
  }
}

function handleRestrictUpdate(isChecked: boolean) {
  isRestrictedToPreselections.value = isChecked
  if (props.modelValue?.strategy) {
    emit('update:modelValue', {
      strategy: props.modelValue.strategy,
      appName: props.modelValue.appName,
      is_restricted_to_preselections: isChecked,
    })
  }
}

async function handlePreviewClick(appIndex: number) {
  const app = sectionSelectorApps.value[appIndex]

  if (!app) return

  // Use the same data structure as in SectionList.vue
  // Make sure to always include sections array even if empty
  // If canvasId is provided, include it too
  const sectionSelectorData: Record<string, any> = {
    selection_app_name: app.app_metadata?.name,
    sections: props.sectionListData?.sections || [],
    section_list_id: props.sectionListId,
    canvas_id: props.canvasId,
  }

  try {
    if (app.app_metadata?.name && doesLocalOverrideExist(app.app_metadata.name)) {
      selectedAppUrl.value = getLocalOverrideUrl(app.app_metadata.name) as string
      selectedAppUrl.value = selectedAppUrl.value.replace('index.html', 'edit.html')
      isCustomModalOpen.value = true

      // Wait for iframe to be mounted
      await nextTick()

      // Set up communication with iframe
      waitForIframeInitialize(
        iframeRef.value,
        sectionSelectorData,
        'section-selector-preview',
        (selectedData?: { section_ids?: string[]; form_data?: Record<string, any> }) => {
          console.info('[SectionListStrategySelector]: Received done callback with data', selectedData)
          isCustomModalOpen.value = false
        },
        { componentName: 'SectionListStrategySelector' },
      )
      return
    }
  } catch (e) {
    console.error('Error generating Embeddable debug url', e)
  }

  if (app.content_url) {
    selectedAppUrl.value = app.content_url
    selectedAppUrl.value = selectedAppUrl.value.replace('index.html', 'edit.html')
    isCustomModalOpen.value = true

    // Wait for iframe to be mounted
    await nextTick()

    // Set up communication with iframe
    waitForIframeInitialize(
      iframeRef.value,
      sectionSelectorData,
      'section-selector-preview',
      (selectedData?: { section_ids?: string[]; form_data?: Record<string, any> }) => {
        console.info('[SectionListStrategySelector]: Received done callback with data', selectedData)
        isCustomModalOpen.value = false
      },
      { componentName: 'SectionListStrategySelector' },
    )
  }
}

function renderLabel(option: { label: string; isApp: boolean; value: string | number }) {
  return h('div', { class: 'flex items-center gap-2' }, [
    option.label,
    option.isApp &&
      h(
        NButton,
        {
          size: 'tiny',
          onClick: (e: Event) => {
            e.stopPropagation()
            handlePreviewClick(option.value as number)
          },
        },
        { default: () => t('canvasUI.strategy.configure') },
      ),
  ])
}

// Initialize isRestrictedToPreselections from props on component load
onMounted(() => {
  isRestrictedToPreselections.value = !!props.modelValue?.is_restricted_to_preselections
})
</script>
